/**
 * Preview Facility filter (Register Import Wizard, Map Fields → Preview). Only offered when rows are
 * assigned by Facility (Separated, or a first import with a Facility column).
 *
 * Rows are grouped by their RESOLVED Facility — the same resolution the Preview uses for its
 * "Facility not matched" skips (alias first, then exact name; registerImportWizardModel
 * .resolveFacilityValue) — never by the raw cell text, so "PUQ", " puq " and an alias of PUQ are one
 * option. Blank, unknown, ambiguous and stale values share one "No matching Facility" option: the
 * server skips those rows.
 *
 * Selection model: `null` = every option (the default, and what any "all selected" state collapses
 * back to); otherwise an array of option keys. Pure functions — no Vue.
 */
import { FACILITY_MATCH, isBlankCell, normalizeFacilityKey, resolveFacilityValue } from '../../../modules/registerImportWizardModel.js';

/** Option key for rows whose Facility is blank or does not resolve (they are skipped). */
export const FACILITY_FILTER_NONE = '__none__';

const RESOLVED = new Set([FACILITY_MATCH.EXACT, FACILITY_MATCH.ALIAS]);
const fmt = (value) => Number(value || 0).toLocaleString('en-US');

/**
 * One option per resolved Facility (in the Facility Group's member order) plus, when any row has no
 * matching Facility, a trailing "No matching Facility" option. Counts cover every row.
 * @param {Array<object>} rows - parsed rows
 * @param {{column:string, aliases:Array, members:Array<{id:string,name:string}>}} facility
 * @returns {{rowKeys:string[], options:Array<{key:string,label:string,rows:number,none:boolean}>}}
 */
export function buildFacilityFilterModel(rows, facility) {
  const list = rows || [];
  if (!facility || !facility.column) return { rowKeys: list.map(() => FACILITY_FILTER_NONE), options: [] };
  const members = facility.members || [];
  const cache = new Map();
  const keyOf = (raw) => {
    const cacheKey = isBlankCell(raw) ? '' : normalizeFacilityKey(raw);
    if (!cache.has(cacheKey)) {
      const match = resolveFacilityValue(raw, facility.aliases, members);
      cache.set(cacheKey, RESOLVED.has(match.kind) && match.facilityId ? match.facilityId : FACILITY_FILTER_NONE);
    }
    return cache.get(cacheKey);
  };
  const counts = new Map();
  const rowKeys = list.map((row) => {
    const key = keyOf(row ? row[facility.column] : undefined);
    counts.set(key, (counts.get(key) || 0) + 1);
    return key;
  });
  const options = members
    .filter((member) => counts.has(member.id))
    .map((member) => ({ key: member.id, label: member.name, rows: counts.get(member.id), none: false }));
  if (counts.has(FACILITY_FILTER_NONE)) {
    options.push({ key: FACILITY_FILTER_NONE, label: 'No matching Facility', rows: counts.get(FACILITY_FILTER_NONE), none: true });
  }
  return { rowKeys, options };
}

/** The option keys currently shown (a stale key — no longer an option — is ignored). */
export function selectedFacilityKeys(selection, options) {
  const keys = (options || []).map((option) => option.key);
  return selection === null || selection === undefined ? keys : keys.filter((key) => selection.includes(key));
}

/** True when the filter hides at least one option. */
export function isFacilityFilterActive(selection, options) {
  return selectedFacilityKeys(selection, options).length < (options || []).length;
}

/** "All Facilities" checkbox state: 'all' | 'some' (indeterminate) | 'none'. */
export function allFacilitiesState(selection, options) {
  const selected = selectedFacilityKeys(selection, options).length;
  if (selected === (options || []).length) return 'all';
  return selected ? 'some' : 'none';
}

/** Tick or untick one option. Selecting every option collapses back to `null` (all). */
export function toggleFacilityKey(selection, options, key, checked) {
  const current = selectedFacilityKeys(selection, options);
  const next = checked ? [...new Set([...current, key])] : current.filter((k) => k !== key);
  return next.length === (options || []).length ? null : next;
}

/** "All Facilities": selects every option, or clears them when every option is already selected. */
export function toggleAllFacilities(selection, options) {
  return allFacilitiesState(selection, options) === 'all' ? [] : null;
}

/** Trigger text: All Facilities / the single name / "N Facilities" / "No Facilities". */
export function facilityFilterLabel(selection, options) {
  const selected = selectedFacilityKeys(selection, options);
  if (selected.length === (options || []).length) return 'All Facilities';
  if (!selected.length) return 'No Facilities';
  if (selected.length === 1) return options.find((option) => option.key === selected[0]).label;
  return `${selected.length} Facilities`;
}

/**
 * Stable identity of the grouping (options + their row counts). A Facility column or name-mapping
 * change moves rows between options, so the selection resets when this changes.
 */
export function facilityFilterSignature(options) {
  return (options || []).map((option) => `${option.key}:${option.rows}`).join('|');
}

/**
 * The line under the preview table. Null when every row is rendered and nothing is filtered.
 * @param {{visible:number, matched:number, total:number, filtered:boolean}} counts
 *   visible = rendered rows, matched = rows passing the filters, total = rows in the file
 */
export function previewCapText({ visible, matched, total, filtered }) {
  const truncated = matched > visible;
  if (!filtered) {
    return truncated ? `Showing the first ${fmt(visible)} of ${fmt(matched)} rows. The counts above cover every row.` : null;
  }
  return truncated
    ? `Showing the first ${fmt(visible)} of ${fmt(matched)} matching rows (${fmt(total)} in the file). The counts above cover every row.`
    : `Showing ${fmt(matched)} of ${fmt(total)} rows. The counts above cover every row.`;
}
